import type React from 'react';
import type { StateProvinceApiRow } from '@/api/reference-admin';
import { stateProvinceEntityConfig } from '@/config/reference-admin-entities';
import { formatDate } from '@/lib/utils';
import {
  type ColumnConfig,
  DataTable,
  type DataTableResponse,
  type FilterConfig,
} from './data-table';

import { DeleteReferenceAdminDialog } from './modals/delete-reference-admin-dialog';
import { EditReferenceAdminModal } from './modals/edit-reference-admin-modal';
import { ReferenceAdminStatus, StatusChip } from './status-chip';
import { type SubdivisionType, SubdivisionTypeChip } from './subdivision-type-chip';

// Export the StateProvinceApiRow type as StateProvinceRow for consistency
export type StateProvinceRow = StateProvinceApiRow;

interface Props {
  data?: DataTableResponse<StateProvinceApiRow> | undefined;
  isLoading?: boolean;
  error?: string | null;
  onFilterChange?: (filterKey: string, value: string | string[]) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange?: (page: number) => void;
  onRefresh?: () => void;
  headerActions?: React.ReactNode;
}

export const StatesProvincesTable: React.FC<Props> = ({
  data,
  isLoading = false,
  error = null,
  onFilterChange,
  onSortChange,
  onPageChange,
  onRefresh,
  headerActions,
}) => {
  const columns: ColumnConfig<StateProvinceApiRow>[] = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (item) => item.code,
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (item) => item.name,
    },
    {
      key: 'country',
      label: 'Country',
      sortable: true,
      render: (item) => item.country?.name || '—',
    },
    {
      key: 'subdivision_type',
      label: 'Type',
      sortable: true,
      align: 'center',
      render: (item) =>
        item.subdivision_type ? (
          <SubdivisionTypeChip subdivisionType={item.subdivision_type as SubdivisionType} />
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      align: 'center',
      render: (item) => (
        <StatusChip
          type="reference"
          status={item.is_active ? ReferenceAdminStatus.ACTIVE : ReferenceAdminStatus.INACTIVE}
        />
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      align: 'right',
      render: (item) => (
        <span className="text-muted-foreground">
          {item.created_at ? formatDate(item.created_at) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      align: 'center',
      render: (row) => (
        <div className="flex items-center gap-1 justify-center">
          <EditReferenceAdminModal
            config={stateProvinceEntityConfig}
            entity={row}
            onSuccess={onRefresh}
          />
          <DeleteReferenceAdminDialog
            config={stateProvinceEntityConfig}
            entity={row}
            onSuccess={onRefresh}
          />
        </div>
      ),
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      placeholder: 'Select Status',
      multiSelect: false,
      options: [
        { value: ReferenceAdminStatus.ACTIVE, label: 'Active' },
        { value: ReferenceAdminStatus.INACTIVE, label: 'Inactive' },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      <DataTable
        data={data}
        columns={columns}
        caption="States and provinces"
        rowHeaderColumnKey="name"
        filters={filters}
        headerActions={headerActions}
        isLoading={isLoading}
        error={error}
        {...(onFilterChange && { onFilterChange })}
        {...(onSortChange && { onSortChange })}
        {...(onPageChange && { onPageChange })}
        emptyMessage="No states/provinces found"
        loadingMessage="Loading states/provinces..."
        defaultSortKey="name"
        defaultSortOrder="asc"
        compact={true}
      />
    </div>
  );
};
